// src/components/pages/Auth/AuthRegisterForm.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";

import { AuthErrorMessage, toUiAuthError } from "../../GlobalFunction/AuthErrorMessage";

const isTWLocal = (p) => /^09\d{8}$/.test(String(p || "").replace(/\s|-/g, ""));

// allow user type "9xxxxxxxx" and normalize to "09xxxxxxxx"
function normalizeTWPhoneInput(raw) {
  const n = String(raw || "").replace(/\s|-/g, "");
  if (/^9\d{8}$/.test(n)) return "0" + n;
  if (/^09\d{8}$/.test(n)) return n;
  return n;
}

// small helper: mm:ss
function formatMMSS(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

/* -------- password rule helpers -------- */
const initialPasswordRules = {
  length: false,
  notSameAsUsername: false,
  notEasyGuess: false,
  complexity: false,
};

function hasLongNumericSequence(str) {
  const s = str || "";
  if (s.length < 4) return false;

  let ascLen = 1;
  let descLen = 1;

  for (let i = 1; i < s.length; i++) {
    const prev = s[i - 1];
    const curr = s[i];

    if (/[0-9]/.test(prev) && /[0-9]/.test(curr)) {
      const diff = curr.charCodeAt(0) - prev.charCodeAt(0);
      if (diff === 1) {
        ascLen += 1;
        descLen = 1;
      } else if (diff === -1) {
        descLen += 1;
        ascLen = 1;
      } else {
        ascLen = 1;
        descLen = 1;
      }
      if (ascLen >= 4 || descLen >= 4) return true;
    } else {
      ascLen = 1;
      descLen = 1;
    }
  }

  return false;
}

function evaluatePasswordRules(password, opts = {}) {
  const { username = "", phone = "" } = opts;
  const pwd = password || "";
  const uname = (username || "").toLowerCase();
  const phoneDigits = (phone || "").replace(/\D/g, "");

  const length = pwd.length >= 8 && pwd.length <= 20;
  const notSameAsUsername = !pwd || pwd.toLowerCase() !== uname;

  let notEasyGuess = !!pwd;
  if (pwd) {
    const lower = pwd.toLowerCase();
    const common = ["0000", "1111", "qwer", "password"];

    if (uname && uname.length >= 4 && lower.includes(uname)) notEasyGuess = false;

    if (phoneDigits && phoneDigits.length >= 4) {
      const tail4 = phoneDigits.slice(-4);
      if (lower.includes(tail4)) notEasyGuess = false;
    }

    if (common.some((c) => lower.includes(c))) notEasyGuess = false;
    if (hasLongNumericSequence(pwd)) notEasyGuess = false;
    if (/(.)\1\1/.test(pwd)) notEasyGuess = false;
  }

  const hasAsciiLetter = /[A-Za-z]/.test(pwd);
  const hasDigit = /\d/.test(pwd);
  const hasSpecialAscii = /[^A-Za-z0-9]/.test(pwd);
  const hasNonLatin = /[^\u0000-\u007f]/.test(pwd);
  const hasSymbolOrOther = hasSpecialAscii || hasNonLatin;
  const hasUpper = /[A-Z]/.test(pwd);
  const hasLower = /[a-z]/.test(pwd);

  const complexity = !!pwd && hasAsciiLetter && hasDigit && hasSymbolOrOther && hasUpper && hasLower;

  return { length, notSameAsUsername, notEasyGuess, complexity };
}

const PASSWORD_RULE_ITEMS = [
  { key: "length", labelKey: "pwdRule1" },
  { key: "notSameAsUsername", labelKey: "pwdRule3" },
  { key: "notEasyGuess", labelKey: "pwdRule4" },
  { key: "complexity", labelKey: "pwdRule5" },
];

function getPasswordStrength(password, rules = initialPasswordRules, opts = {}) {
  const pwd = password || "";
  if (!pwd) return { level: "none", score: 0 };

  const { username = "" } = opts;
  const lower = pwd.toLowerCase();
  const uname = (username || "").toLowerCase();

  let score = 0;
  if (rules.length) score += 1;
  if (rules.complexity) score += 2;
  if (rules.notEasyGuess) score += 1;
  if (rules.notSameAsUsername) score += 1;
  if (pwd.length >= 12) score += 1;

  const hasAsciiLetter = /[A-Za-z]/.test(pwd);
  const hasDigit = /\d/.test(pwd);
  const hasSpecialAscii = /[^A-Za-z0-9]/.test(pwd);
  const hasNonLatin = /[^\u0000-\u007f]/.test(pwd);
  const hasSymbolOrOther = hasSpecialAscii || hasNonLatin;

  let typeCount = 0;
  [hasAsciiLetter, hasDigit, hasSymbolOrOther].forEach((b) => {
    if (b) typeCount += 1;
  });
  if (typeCount >= 2) score += 1;

  if (!rules.notEasyGuess || !rules.notSameAsUsername || (uname && lower === uname)) {
    return { level: "weak", score };
  }

  let level = "weak";
  if (score >= 5) level = "strong";
  else if (score >= 3) level = "medium";

  return { level, score };
}

export default function AuthRegisterForm({
  rail,
  lang,
  t,
  loading,
  onSubmitRegister, // async (payload) => void
  onGoLogin, // () => void
  requestEmailOtp, // async ({ purpose, email, lang }) => { expiresIn? }
  verifyEmailOtp, // async ({ purpose, email, code }) => void
}) {
  const [errorObj, setErrorObj] = useState(null);

  // fields
  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");

  // ✅ store as YYYY-MM-DD; send to backend as `birthday`
  const [birthday, setBirthday] = useState(""); // YYYY-MM-DD
  const [gender, setGender] = useState(""); // "male" | "female" | "" (optional)

  // passwords
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [passwordRules, setPasswordRules] = useState(initialPasswordRules);

  // OTP states
  const [otp, setOtp] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpExpiresAtMs, setOtpExpiresAtMs] = useState(0);
  const [otpResendAvailableAtMs, setOtpResendAvailableAtMs] = useState(0);
  const [otpStatusMsg, setOtpStatusMsg] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);

  const otpTickRef = useRef(null);
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    if (otpTickRef.current) clearInterval(otpTickRef.current);
    otpTickRef.current = setInterval(() => setNowTick(Date.now()), 500);
    return () => {
      if (otpTickRef.current) clearInterval(otpTickRef.current);
      otpTickRef.current = null;
    };
  }, []);

  const phoneNormalized = useMemo(() => normalizeTWPhoneInput(phone), [phone]);

  useEffect(() => {
    setPasswordRules(
      evaluatePasswordRules(password, {
        username,
        phone: isTWLocal(phoneNormalized) ? phoneNormalized : "",
      })
    );
  }, [username, phoneNormalized, password]);

  const strength = useMemo(
    () => getPasswordStrength(password, passwordRules, { username }),
    [password, passwordRules, username]
  );

  let strengthColor = "grey.400";
  let strengthWidth = "0%";
  if (strength.level === "weak") {
    strengthColor = "error.main";
    strengthWidth = "33%";
  } else if (strength.level === "medium") {
    strengthColor = "warning.main";
    strengthWidth = "66%";
  } else if (strength.level === "strong") {
    strengthColor = "success.main";
    strengthWidth = "100%";
  }

  const emailTrim = String(email || "").trim();
  const shouldUseOtp = !!emailTrim;

  const otpExpired = otpExpiresAtMs > 0 && nowTick >= otpExpiresAtMs;
  const otpRemainingSec = otpExpiresAtMs ? Math.max(0, Math.ceil((otpExpiresAtMs - nowTick) / 1000)) : 0;

  const resendCooldownSec = otpResendAvailableAtMs
    ? Math.max(0, Math.ceil((otpResendAvailableAtMs - nowTick) / 1000))
    : 0;

  const canSendOtp = shouldUseOtp && !otpVerified && !otpSending && resendCooldownSec <= 0;
  const canVerifyOtp = shouldUseOtp && !otpVerified && !!otp && !otpVerifying;

  // reset otp lock if email changes
  useEffect(() => {
    setOtp("");
    setOtpStatusMsg("");
    setOtpExpiresAtMs(0);
    setOtpResendAvailableAtMs(0);
    setOtpVerified(false);
    setErrorObj(null);
  }, [emailTrim]);

  const otpBorderSx = otpVerified
    ? {
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "success.main" },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "success.main" },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "success.main" },
      }
    : {};

  const otpHelperText = otpStatusMsg ? otpStatusMsg : otpExpired ? t("otpExpired") : t("otpHint");

  const signupSubmitDisabled =
    loading ||
    otpSending ||
    (shouldUseOtp ? !otpVerified : false) ||
    (shouldUseOtp ? otpExpired : false);

  const requiredHandlers = {
    onInvalid: (e) => {
      e.target.setCustomValidity(t("requiredField"));
    },
    onInput: (e) => {
      e.target.setCustomValidity("");
    },
  };

  const uiErrorText = useMemo(() => {
    if (!errorObj) return "";
    return toUiAuthError(errorObj, { lang, t });
  }, [errorObj, lang, t]);

  async function handleSendOtp() {
    if (!emailTrim) {
      setErrorObj(new Error(t("otpNeedEmail")));
      return;
    }
    if (otpVerified || !canSendOtp) return;

    setErrorObj(null);
    setOtpStatusMsg("");
    setOtpSending(true);

    try {
      const payload = await requestEmailOtp({
        purpose: "register",
        email: emailTrim,
        lang,
      });

      const expiresIn = Number(payload?.expiresIn);
      const ttlSec = Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 300;

      const now = Date.now();
      setOtpExpiresAtMs(now + ttlSec * 1000);
      setOtpResendAvailableAtMs(now + 60 * 1000);

      setOtpStatusMsg(t("otpSent"));
    } catch (err) {
      setErrorObj(err);
    } finally {
      setOtpSending(false);
    }
  }

  async function handleVerifyOtpClick() {
    if (!emailTrim) {
      setErrorObj(new Error(t("otpNeedEmail")));
      return;
    }
    if (!otp) {
      setErrorObj(new Error(t("otpRequired")));
      return;
    }
    if (otpVerified || !canVerifyOtp) return;

    setErrorObj(null);
    setOtpStatusMsg("");
    setOtpVerifying(true);

    try {
      await verifyEmailOtp({
        purpose: "register",
        email: emailTrim,
        code: String(otp || "").trim(),
      });

      setOtpVerified(true);
      setOtpStatusMsg(t("otpVerified"));
    } catch (err) {
      setErrorObj(err);
    } finally {
      setOtpVerifying(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (loading) return;

    setErrorObj(null);

    if (!String(username || "").trim()) {
      setErrorObj(new Error(t("signupUsernameRequired")));
      return;
    }

    const local = normalizeTWPhoneInput(phone);
    if (!isTWLocal(local)) {
      setErrorObj(new Error(t("signupPhoneFormatError")));
      return;
    }

    if (!password || password.length < 8 || password.length > 20) {
      setErrorObj(new Error(t("signupPwdMin")));
      return;
    }
    if (password !== password2) {
      setErrorObj(new Error(t("signupPwdMismatch")));
      return;
    }

    if (shouldUseOtp) {
      if (!String(otp || "").trim()) {
        setErrorObj(new Error(t("otpRequired")));
        return;
      }
      if (otpExpired) {
        setErrorObj(new Error(t("otpExpired")));
        return;
      }
      if (!otpVerified) {
        setErrorObj(new Error(t("otpRequired")));
        return;
      }
    }

    try {
      await onSubmitRegister({
        username: String(username || "").trim(),
        phoneNumber: local,
        email: emailTrim || "",
        emailOtp: shouldUseOtp ? String(otp || "").trim() : undefined,
        password,
        firstName: String(firstName || "").trim(),
        lastName: String(lastName || "").trim(),

        // ✅ IMPORTANT: backend expects `birthday`, not `birthdate`
        birthday: birthday || undefined, // "YYYY-MM-DD"
        gender: gender || undefined, // "male" | "female" | ...
      });

      // clear form after success (AuthPage will show success dialog)
      setPassword("");
      setPassword2("");
      setOtp("");
      setOtpStatusMsg("");
      setOtpExpiresAtMs(0);
      setOtpResendAvailableAtMs(0);
      setOtpVerified(false);

      // optional clears
      setUsername("");
      setPhone("");
      setFirstName("");
      setLastName("");
      setEmail("");
      setBirthday("");
      setGender("");
      setErrorObj(null);
    } catch (err) {
      setErrorObj(err);
    }
  }

  // Labels: prefer original translation keys if they exist, fallback to current
  const tx = (key, fallback) => {
    if (typeof t === "function") {
      const out = t(key);
      if (out && out !== key) return out;
    }
    return fallback;
  };

  const genderLabel = tx("signupGender", lang === "JP" ? "性別" : lang === "ZH" ? "性別" : "Gender");
  const birthdayLabel = tx("signupBirthday", lang === "JP" ? "生年月日" : lang === "ZH" ? "生日" : "Birthday");

  const genderOptions = [
    { v: "male", label: tx("signupGenderMale", lang === "JP" ? "男性" : lang === "ZH" ? "男" : "Male") },
    { v: "female", label: tx("signupGenderFemale", lang === "JP" ? "女性" : lang === "ZH" ? "女" : "Female") },
    { v: "prefer_not", label: tx("signupGenderPreferNot", lang === "JP" ? "回答しない" : lang === "ZH" ? "不透露" : "Prefer not to say") },
  ];

  return (
    <Stack spacing={1.5} component="form" onSubmit={handleSubmit}>
      <Typography variant="h5" sx={{ mb: 0.5, textAlign: "center", fontWeight: 600 }}>
        {t("signupTitle")}
      </Typography>

      <TextField
        size="small"
        label={t("usernameLabel")}
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        fullWidth
        required
        autoComplete="username"
        {...requiredHandlers}
      />

      <TextField
        size="small"
        label={lang === "ZH" ? "手機號碼" : lang === "JP" ? "携帯電話番号" : "Phone number"}
        value={phone}
        onChange={(e) => setPhone(normalizeTWPhoneInput(e.target.value))}
        fullWidth
        required
        autoComplete="tel"
        helperText={phone && !isTWLocal(phoneNormalized) ? t("phoneFormatShort") : " "}
        error={!!phone && !isTWLocal(phoneNormalized)}
        inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 10 }}
        {...requiredHandlers}
      />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
        <TextField
          size="small"
          label={t("signupLastName")}
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          fullWidth
          autoComplete="family-name"
        />
        <TextField
          size="small"
          label={t("signupFirstName")}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          fullWidth
          autoComplete="given-name"
        />
      </Box>

      {/* ✅ Birthday + Gender */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
        <TextField
          size="small"
          label={birthdayLabel}
          type="date"
          value={birthday}
          onChange={(e) => setBirthday(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label={genderLabel}
          value={gender}
          onChange={(e) => setGender(e.target.value)}
          fullWidth
          select
        >
          {genderOptions.map((o) => (
            <MenuItem key={o.v} value={o.v}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <TextField
        size="small"
        label={t("signupEmail")}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        fullWidth
        autoComplete="email"
        disabled={otpVerified}
        helperText={
          otpVerified
            ? lang === "ZH"
              ? "Email 已完成驗證。若要更換 Email，請先修改 Email 後重新驗證。"
              : lang === "JP"
              ? "メールは認証済みです。変更する場合はメールを編集して再認証してください。"
              : "Email is verified. To change it, edit the email and verify again."
            : " "
        }
      />

      {/* OTP section only when email exists */}
      {shouldUseOtp && (
        <Box sx={{ mt: 0.25 }}>
          <Divider sx={{ my: 1 }} />
          <Stack spacing={1}>
            <TextField
              size="small"
              label={t("otpLabel")}
              value={otp}
              onChange={(e) => {
                if (otpVerified) return;
                const v = String(e.target.value || "").replace(/\D/g, "");
                setOtp(v.slice(0, 6));
              }}
              fullWidth
              disabled={otpVerified}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 6 }}
              helperText={otpHelperText}
              FormHelperTextProps={{
                sx: {
                  mt: 0.5,
                  color: otpVerified ? "success.main" : "text.secondary",
                  fontWeight: otpVerified ? 700 : 400,
                },
              }}
              sx={otpBorderSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MarkEmailReadRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {!!otpExpiresAtMs && !otpExpired && !otpVerified && (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mr: 0.5 }}>
                          <AccessTimeRoundedIcon sx={{ fontSize: 16 }} />
                          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                            {t("otpTimerPrefix")} {formatMMSS(otpRemainingSec)}
                          </Typography>
                        </Stack>
                      )}

                      {otpVerified && (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mr: 0.25 }}>
                          <CheckCircleRoundedIcon sx={{ fontSize: 18, color: "success.main" }} />
                          <Typography sx={{ fontSize: 12, color: "success.main", fontWeight: 800 }}>
                            {t("otpVerified")}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </InputAdornment>
                ),
              }}
            />

            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                type="button"
                variant="outlined"
                onClick={handleSendOtp}
                disabled={!canSendOtp}
                startIcon={otpExpiresAtMs ? <ReplayRoundedIcon /> : <SendRoundedIcon />}
                sx={{ whiteSpace: "nowrap" }}
              >
                {otpExpiresAtMs ? t("otpResend") : t("otpSend")}
                {resendCooldownSec > 0 ? ` (${resendCooldownSec}s)` : ""}
              </Button>

              <Button
                type="button"
                variant="text"
                onClick={handleVerifyOtpClick}
                disabled={!canVerifyOtp}
                sx={{ whiteSpace: "nowrap" }}
              >
                {otpVerifying ? "..." : lang === "ZH" ? "驗證" : lang === "JP" ? "確認" : "Verify"}
              </Button>
            </Stack>

            {otpVerified && (
              <Typography sx={{ fontSize: 12, color: "success.main", fontWeight: 700, mt: 0.25 }}>
                {lang === "ZH"
                  ? "已完成 Email 驗證，註冊按鈕已解鎖。"
                  : lang === "JP"
                  ? "メール認証が完了しました。登録ボタンが有効になりました。"
                  : "Email verified. The Sign up button is now enabled."}
              </Typography>
            )}
          </Stack>
          <Divider sx={{ my: 1 }} />
        </Box>
      )}

      <TextField
        size="small"
        type={showPwd ? "text" : "password"}
        label={t("passwordLabel")}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        fullWidth
        required
        autoComplete="new-password"
        inputProps={{ spellCheck: "false", autoCapitalize: "none", autoCorrect: "off" }}
        {...requiredHandlers}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockOutlinedIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showPwd ? t("hidePassword") : t("showPassword")}
                edge="end"
                size="small"
                onClick={() => setShowPwd((v) => !v)}
              >
                {showPwd ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* strength + rules */}
      {password && (
        <Box sx={{ mt: 0.25 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {lang === "ZH" ? "密碼強度：" : lang === "JP" ? "パスワード強度：" : "Password strength:"}
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: strengthColor }}>
              {strength.level === "strong"
                ? lang === "ZH"
                  ? "強"
                  : lang === "JP"
                  ? "強い"
                  : "Strong"
                : strength.level === "medium"
                ? lang === "ZH"
                  ? "中"
                  : lang === "JP"
                  ? "普通"
                  : "Medium"
                : strength.level === "weak"
                ? lang === "ZH"
                  ? "弱"
                  : lang === "JP"
                  ? "弱い"
                  : "Weak"
                : "-"}
            </Typography>
          </Stack>

          <Box sx={{ height: 8, borderRadius: 999, bgcolor: "grey.200", overflow: "hidden", mb: 0.5 }}>
            <Box sx={{ height: "100%", width: strengthWidth, transition: "width 0.25s ease", bgcolor: strengthColor }} />
          </Box>

          {PASSWORD_RULE_ITEMS.map((item) => {
            const passed = passwordRules[item.key];
            const Icon = passed ? CheckCircleRoundedIcon : CancelRoundedIcon;
            return (
              <Stack key={item.key} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                <Icon sx={{ fontSize: 18, color: passed ? "success.main" : "error.main" }} />
                <Typography variant="caption" sx={{ color: passed ? "success.main" : "error.main", fontSize: 12 }}>
                  {t(item.labelKey)}
                </Typography>
              </Stack>
            );
          })}
        </Box>
      )}

      <TextField
        size="small"
        type={showPwd2 ? "text" : "password"}
        label={t("confirmPasswordLabel")}
        value={password2}
        onChange={(e) => setPassword2(e.target.value)}
        fullWidth
        required
        autoComplete="new-password"
        inputProps={{ spellCheck: "false", autoCapitalize: "none", autoCorrect: "off" }}
        {...requiredHandlers}
        error={!!password2 && password !== password2}
        helperText={!!password2 && password !== password2 ? t("signupPwdMismatch") : " "}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockOutlinedIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showPwd2 ? t("hidePassword") : t("showPassword")}
                edge="end"
                size="small"
                onClick={() => setShowPwd2((v) => !v)}
              >
                {showPwd2 ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* ✅ Error now always shows */}
      <AuthErrorMessage text={uiErrorText} variant="alert" sx={{ mt: 0.5 }} />

      <Button
        type="submit"
        variant="contained"
        disabled={signupSubmitDisabled}
        size="large"
        sx={{ mt: 0.5, bgcolor: rail, opacity: signupSubmitDisabled ? 0.7 : 1 }}
      >
        {t("signupButton")}
      </Button>

      {shouldUseOtp && !otpVerified && (
        <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
          {lang === "ZH"
            ? "請先完成 Email 驗證後才能註冊。"
            : lang === "JP"
            ? "登録するにはメール認証が必要です。"
            : "Please verify your email OTP before signing up."}
        </Typography>
      )}

      {/* Go login */}
      <Stack direction="row" justifyContent="center" sx={{ mt: 0.5 }}>
        <Button type="button" variant="text" disabled={loading} onClick={onGoLogin} sx={{ textTransform: "none" }}>
          {t("tabLogin")}
        </Button>
      </Stack>
    </Stack>
  );
}
